import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import {
  pointHistoryService,
  PointTransaction,
} from "../../services/point-history.service";

function formatReason(reason: string): string {
  return reason.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

const UserPointHistoryPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation("common");

  const [balance, setBalance] = useState<number | null>(null);
  const [rows, setRows] = useState<PointTransaction[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (p: number) => {
      if (!userId) return;
      setLoading(true);
      setError("");
      try {
        const res = await pointHistoryService.getTransactions(userId, p);
        setBalance(res.balance);
        setRows(res.data);
        setPage(res.meta.page);
        setPageCount(res.meta.pageCount);
        setTotal(res.meta.total);
      } catch {
        setError(t("failed_to_load"));
      } finally {
        setLoading(false);
      }
    },
    [userId, t],
  );

  useEffect(() => { load(1); }, [load]);

  const thClass =
    "px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider";
  const tdClass = "px-4 py-3 text-sm text-gray-800 dark:text-gray-200";

  return (
    <Layout title={t("point_history")} showBackButton>
      <div className="space-y-4">
        {/* Balance card */}
        {balance !== null && (
          <Card className="p-4 flex items-center gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t("point_balance")}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {balance.toLocaleString()} pts
              </p>
            </div>
            <div className="ml-auto text-xs text-gray-400 break-all">{userId}</div>
          </Card>
        )}

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Transactions table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-gray-400 text-sm">{t("loading")}</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">{t("noData")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className={thClass}>{t("created_at")}</th>
                    <th className={thClass}>{t("type")}</th>
                    <th className={thClass}>{t("amount")}</th>
                    <th className={thClass}>{t("reason")}</th>
                    <th className={thClass}>{t("topup_package")}</th>
                    <th className={thClass}>{t("balance_after")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className={tdClass}>
                        {new Date(tx.createdAt).toLocaleString()}
                      </td>
                      <td className={tdClass}>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            tx.type === "EARN"
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                          }`}
                        >
                          {tx.type === "EARN" ? t("earn") : t("spend")}
                        </span>
                      </td>
                      <td className={tdClass}>
                        <span
                          className={
                            tx.amount > 0
                              ? "text-green-600 dark:text-green-400 font-medium"
                              : "text-red-600 dark:text-red-400 font-medium"
                          }
                        >
                          {tx.amount > 0 ? "+" : ""}
                          {tx.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className={tdClass}>
                        <span className="text-gray-600 dark:text-gray-400">
                          {formatReason(tx.reason)}
                        </span>
                      </td>
                      <td className={tdClass}>
                        {tx.topUpPackageId ? (
                          <span className="text-xs text-gray-700 dark:text-gray-300">
                            {tx.topUpPoints?.toLocaleString()} pts
                            {tx.topUpBonusPoints ? (
                              <span className="text-blue-500 dark:text-blue-400">
                                {" "}+{tx.topUpBonusPoints.toLocaleString()} {t("bonus")}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className={tdClass}>
                        {tx.balanceAfter.toLocaleString()} pts
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && pageCount > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {total} {t("results")}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => load(page - 1)}
                  disabled={page <= 1}
                >
                  {t("previous")}
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center px-2">
                  {page} {t("of")} {pageCount}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => load(page + 1)}
                  disabled={page >= pageCount}
                >
                  {t("next")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default UserPointHistoryPage;
